import React, { useEffect } from 'react';
import {
  useChatStore,
  type Message as MessageType,
} from '../../store/chatStore';
import { Message } from './Message';
import { LoadingState } from './LoadingState';
import { useScrollAnchoring } from '../../../../hooks/useScrollAnchoring';
import { DownArrowIndicator } from '../../../common/DownArrowIndicator';
import { cn } from '../../../../lib/utils';

/**
 * Tools whose tool-use blocks render as rich visual content (terminal,
 * dashboard, etc.) — these should NOT be bridged by the stepper connector
 * because their heights vary and their icon column doesn't sit at x=19px.
 * Mirrors RICH_VISUAL_TOOLS in Message.tsx.
 */
const NON_BRIDGEABLE_TOOLS = new Set([
  'GetDashboard',
  'ManageDashboard',
  'GenerateReport',
  'RenderHTML',
  'writeFiles',
  'shareFile',
  'printFile',
  'listFiles',
  'copyFile',
  'deleteFile',
  'readFile',
  'editFile',
  'shellRun',
  'listPleriSkills',
  'createPleriSkill',
  'updatePleriSkill',
  'deletePleriSkill',
  'forkTemplate',
  'shellAssumeRole',
  'shellListAssumableRoles',
  'askQuestion',
  'presentPlan',
  'updatePlanProgress',
  'analyzeExploitability',
  'cloudShellRun',
  'cloudShellWriteFiles',
  'cloudShellReadFile',
  'cloudShellEditFile',
  'cloudShellListFiles',
  'cloudShellGetDownloadLink',
  'cloudShellPrint',
  'cloudShellGlob',
  'cloudShellPublish',
  'workspaceWriteFiles',
  'workspaceReadFile',
  'workspaceEditFile',
  'workspaceListFiles',
  'workspaceDeleteFile',
  'workspaceGetDownloadLink',
  'workspacePrint',
  'stageSkillForShell',
  'stageFileForShell',
]);

/** True when the LAST visible block of a message is a compact tool pill. */
function lastBlockIsBridgeable(msg: MessageType): boolean {
  const visible = msg.content.filter((b) => b.type !== 'toolResult');
  if (!visible.length) {
    return false;
  }
  const last = visible[visible.length - 1];
  if (last.type !== 'toolUse') {
    return false;
  }
  return !NON_BRIDGEABLE_TOOLS.has(last.name);
}

/** True when the FIRST visible block of a message is a compact tool pill. */
function firstBlockIsBridgeable(msg: MessageType): boolean {
  const visible = msg.content.filter((b) => b.type !== 'toolResult');
  if (!visible.length) {
    return false;
  }
  const first = visible[0];
  if (first.type !== 'toolUse') {
    return false;
  }
  return !NON_BRIDGEABLE_TOOLS.has(first.name);
}

interface MessageListProps {
  onToolExpand?: () => void;
  /**
   * If true, the tool use block will not collapse when the tool is expanded.
   * This is useful for the chat area in the small chat window.
   */
  preventToolUseBlockToCollapse?: boolean;
  isReadOnly?: boolean;
  showMessageFromIndex?: number;
  /**
   * If true, the message list will automatically scroll to the bottom on updates.
   * Defaults to true.
   */
  autoScrollToBottom?: boolean;
  /**
   * Whether to show the down arrow indicator
   */
  showDownIndicator?: boolean;
  /**
   * ID of the scrollable container (the parent div with overflow-y-auto)
   */
  scrollableContainerId?: string;
}

export const MessageList: React.FC<MessageListProps> = ({
  onToolExpand,
  preventToolUseBlockToCollapse,
  isReadOnly,
  showMessageFromIndex = 0,
  autoScrollToBottom = true,
  showDownIndicator = true,
  scrollableContainerId,
}) => {
  // Use the enhanced scroll anchoring hook with down arrow indicator
  const {
    containerRef,
    anchorRef,
    containerClassName,
    anchorClassName,
    showDownIndicator: shouldShowIndicator,
    scrollToBottom,
  } = useScrollAnchoring({
    enabled: autoScrollToBottom,
    immediate: false, // Let user scroll first, then anchor
    showDownIndicator,
    containerClass: 'message-list-scroll-container',
    scrollableContainerId,
    scrollToBottomOnLoad: true, // Scroll to bottom when content first loads
  });

  const messages = useChatStore((state) => {
    const activeConversation = state.activeConversationId
      ? state.conversations[state.activeConversationId]
      : null;
    return activeConversation?.messages || [];
  });

  const isLoading = useChatStore((state) => {
    const activeConversation = state.activeConversationId
      ? state.conversations[state.activeConversationId]
      : null;
    return activeConversation?.isLoading || false;
  });

  const focusChatInput = useChatStore((state) => state.focusChatInput);

  const lastMessage = messages[messages.length - 1];

  useEffect(() => {
    if (lastMessage?.role === 'user') {
      scrollToBottom();
    }
  }, [lastMessage, scrollToBottom]);

  const handleDownArrowClick = () => {
    scrollToBottom();
    focusChatInput();
  };

  if (messages.length === 0) {
    return null;
  }

  const visibleMessages = messages
    .slice(showMessageFromIndex)
    .filter((message) => message.content.length > 0);

  return (
    <>
      <div ref={containerRef} className={cn(containerClassName, 'relative')}>
        {visibleMessages.map((message, index) => {
          const isLastMessage = message.id === lastMessage?.id;
          const nextMessage = visibleMessages[index + 1];

          // Bridge the mb-6 gap between two consecutive messages when both
          // have compact pill-style tool items at their shared boundary.
          // The connector is absolutely positioned so it doesn't affect layout.
          // top: -28 → starts 4px inside Message N's mb-6 tail (near the last pill)
          // height: 52 → spans the 24px gap + 28px into Message N+1's first pill
          // gradient from-transparent keeps the entrance/exit soft.
          const bridge =
            nextMessage &&
            lastBlockIsBridgeable(message) &&
            firstBlockIsBridgeable(nextMessage) ? (
              <div
                className="pointer-events-none relative"
                style={{ height: 0, overflow: 'visible' }}
              >
                <div
                  className="absolute w-px bg-gradient-to-b from-transparent via-gray-400/75 to-transparent dark:from-transparent dark:via-gray-500/60 dark:to-transparent"
                  style={{ left: 19, top: -28, height: 52, zIndex: 10 }}
                />
              </div>
            ) : null;

          return (
            <React.Fragment key={message.id ?? index}>
              <Message
                index={index}
                message={{ ...message }}
                isLoading={isLastMessage && isLoading}
                onToolExpand={onToolExpand}
                isLastMessage={isLastMessage}
                preventToolUseBlockToCollapse={preventToolUseBlockToCollapse}
                isReadOnly={isReadOnly}
              />
              {bridge}
            </React.Fragment>
          );
        })}

        <LoadingState
          isLoading={isLoading}
          hasMessages={messages.length > 0}
          lastMessageIsUser={lastMessage?.role === 'user'}
          startTime={lastMessage?.startTime}
          tokenUsage={lastMessage?.tokenUsage}
        />

        {/* Scroll anchor element - this is what keeps us pinned to the bottom */}
        <div ref={anchorRef} className={anchorClassName} />
      </div>{' '}
      <DownArrowIndicator
        show={shouldShowIndicator}
        onClick={handleDownArrowClick}
        position="bottom-center"
        clickable={true}
      />
      {/* Down arrow indicator */}
    </>
  );
};
